<template>
  <div class="q-pa-xl">
    <div class="flex justify-between items-center q-mb-lg">
      <h3 class="my-events-title">Mina event</h3>
      <q-btn
        label="Skapa event"
        color="primary"
        @click="openCreate"
        class="q-mb-md q-ml-md"
      />
    </div>
    <q-table :rows="events" :columns="columns" row-key="_id" class="q-mb-md">
      <template v-slot:body-cell="props">
        <q-td :props="props">
          <q-img
            v-if="props.col.name === 'imageUrl'"
            :src="props.row.imageUrl"
            alt="Event image"
            style="max-width: 100px; height: auto"
          />
          <div v-else-if="props.col.name === 'title'" class="text-h5 text-left">
            {{ props.row.title }}
          </div>
          <div v-else-if="props.col.name === 'date'" class="text-subtitle1">
            {{ props.row.date ? props.row.date.split("T")[0] : "—" }}
          </div>
          <div v-else-if="props.col.name === 'genre'">
            {{ props.row.genre || "—" }}
          </div>
          <q-btn
            v-else-if="props.col.name === 'link'"
            :label="props.row.link"
            :href="props.row.link"
            target="_blank"
            color="primary"
            flat
          />
          <q-btn
            v-else-if="props.col.name === 'edit'"
            icon="edit"
            color="primary"
            flat
            dense
            @click="openEdit(props.row)"
          />
          <q-btn
            v-else-if="props.col.name === 'delete'"
            icon="delete"
            color="negative"
            flat
            dense
            @click="confirmDelete(props.row._id)"
          />
        </q-td>
      </template>
    </q-table>

    <q-dialog v-model="isDialogOpen" persistent>
      <q-card style="width: 700px; max-width: 80vw">
        <q-card-section>
          <div class="text-h6">
            {{ isEditMode ? "Redigera event" : "Lägg till nytt event" }}
          </div>
        </q-card-section>

        <q-card-section>
          <q-form
            ref="eventForm"
            greedy
            @submit.prevent="submitForm"
            @validation-error="onValidationError"
          >
            <q-input
              v-model="form.title"
              label="Namn"
              outlined
              class="q-mb-md"
              lazy-rules
              :rules="[(val) => !!val || 'Namn är obligatoriskt']"
            />
            <q-input
              v-model="form.date"
              type="date"
              outlined
              class="q-mb-md"
              lazy-rules
              :rules="[(val) => !!val || 'Datum är obligatoriskt']"
            />

            <q-file
              filled
              :label="isEditMode ? 'Byt bild (valfritt)' : 'Välj bild'"
              accept="image/*"
              v-model="selectedFiles"
              class="q-mb-md"
              lazy-rules
              :rules="imageRules"
            />
            <q-img
              v-if="imagePreview"
              :src="imagePreview"
              alt="Bild förhandsvisning"
              style="max-width: 100px; height: auto"
              class="q-mb-md"
            />

            <q-checkbox
              v-model="form.hasEventLink"
              label="Finns länk till information om event?"
              class="q-mb-md"
            />

            <template v-if="form.hasEventLink">
              <q-input
                v-model="form.link"
                label="Länk till eventet"
                outlined
                class="q-mb-md"
                lazy-rules
                :rules="[(val) => !!val || 'Länk till event är obligatoriskt']"
              />
            </template>
            <template v-else>
              <q-input
                v-model="form.eventInfo"
                label="Eventinformation"
                outlined
                type="textarea"
                class="q-mb-md"
                lazy-rules
                :rules="[(val) => !!val || 'Eventinformation är obligatoriskt']"
              />
              <q-input
                v-model="form.eventPrice"
                label="Pris (valfritt)"
                outlined
                class="q-mb-md"
              />
              <q-input
                v-model="form.ticketLink"
                label="Biljettlänk (valfritt)"
                outlined
                class="q-mb-md"
              />
            </template>

            <q-select
              v-model="form.genre"
              :options="genreOptions"
              label="Genre"
              hint="Frivilligt - används för filtrering i publika flödet"
              outlined
              clearable
              emit-value
              map-options
              class="q-mb-md"
            />

            <q-btn
              :label="isEditMode ? 'Spara ändringar' : 'Skicka'"
              type="submit"
              color="primary"
              :loading="saving"
              class="full-width"
            />
          </q-form>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Avbryt" color="grey-7" @click="closeDialog" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "vue";
import { useQuasar } from "quasar";
import axios from "axios";
import { useRouter } from "vue-router";
import { clearAuth } from "@/utils/auth";

const emptyForm = () => ({
  title: "",
  date: "",
  hasEventLink: true,
  link: "",
  eventInfo: "",
  eventPrice: "",
  ticketLink: "",
  genre: null,
});

export default {
  name: "AdminMyEvents",
  setup() {
    const $q = useQuasar();
    const router = useRouter();

    const events = ref([]);
    const form = ref(emptyForm());
    const editingId = ref(null);
    const imageFile = ref(null);
    const imagePreview = ref("");
    const selectedFiles = ref(null);
    const isDialogOpen = ref(false);
    const saving = ref(false);
    const genreOptions = ref([]);
    const eventForm = ref(null);

    const isEditMode = computed(() => editingId.value !== null);

    // Bild är obligatoriskt vid uppskapande men inte vid redigering -
    // redigering behåller den existerande bilden om ingen ny väljs.
    const imageRules = computed(() =>
      isEditMode.value ? [] : [(val) => !!val || "Bild är obligatoriskt"]
    );

    // Visas när formvalideringen failar. Quasar visar redan inline-felen
    // under varje fält - toast:en är en sammanfattande nudge så användaren
    // ser den även om aktuellt scroll-läge gömmer felet.
    const onValidationError = () => {
      $q.notify({
        type: "negative",
        message: "Fyll i alla obligatoriska fält",
        position: "top",
        timeout: 2500,
      });
    };

    const columns = [
      { name: "imageUrl", label: "Bild", align: "center" },
      { name: "title", label: "Namn", align: "center" },
      { name: "date", label: "Datum", align: "center" },
      { name: "genre", label: "Genre", align: "center", field: "genre" },
      { name: "link", label: "Länk", align: "center" },
      { name: "edit", label: "Redigera", align: "center" },
      { name: "delete", label: "Radera", align: "center" },
    ];

    const authHeader = () => {
      const token = localStorage.getItem("token");
      return { Authorization: `Bearer ${token}` };
    };

    const handleAuthError = (err) => {
      const status = err?.response?.status;
      if (status === 401) {
        // Token saknas/utgången - kicka till login
        $q.notify({ type: "negative", message: "Logga in igen." });
        clearAuth();
        router.push("/login");
        return true;
      }
      if (status === 403) {
        // Inloggad men har inte rätten - behåll sessionen, skicka tillbaka
        // till admin-landningen så vi inte fastnar på en otillåten vy.
        $q.notify({
          type: "negative",
          message: "Du har inte behörighet för den här vyn.",
        });
        router.push("/admin");
        return true;
      }
      return false;
    };

    const fetchData = async () => {
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "events/external/my-events",
          { headers: authHeader() }
        );
        events.value = data;
      } catch (err) {
        if (!handleAuthError(err)) {
          $q.notify({
            type: "negative",
            message: "Misslyckades med att hämta data",
          });
        }
      }
    };

    // Genrelistan kommer från en publik endpoint - samma källa som
    // AdminConcerts använder, så super-admin och organizer ser identiska
    // val i sina respektive dropdown:s.
    const fetchGenres = async () => {
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_API_URL + "events/genres"
        );
        genreOptions.value = data.map((g) => ({ label: g, value: g }));
      } catch (err) {
        // Tyst fel - genres är frivilligt, fyll listan med tomt
        genreOptions.value = [];
      }
    };

    // Visa preview av valda lokala bilder. Skapa-objekt-URL:er rensas inte
    // explicit - browsern städar dem när tabben stängs, vilket räcker för
    // det här admin-flow:et.
    watch(selectedFiles, (newVal) => {
      let file = null;
      if (!newVal) {
        file = null;
      } else if (Array.isArray(newVal)) {
        file = newVal[0];
      } else {
        file = newVal;
      }
      if (file && file instanceof Blob) {
        imageFile.value = file;
        imagePreview.value = URL.createObjectURL(file);
      } else {
        imageFile.value = null;
        imagePreview.value = "";
      }
    });

    const openCreate = () => {
      editingId.value = null;
      form.value = emptyForm();
      imageFile.value = null;
      imagePreview.value = "";
      selectedFiles.value = null;
      isDialogOpen.value = true;
    };

    const openEdit = (row) => {
      editingId.value = row._id;
      form.value = {
        title: row.title || "",
        date: row.date ? row.date.split("T")[0] : "",
        hasEventLink: !!row.link,
        link: row.link || "",
        eventInfo: row.eventInfo || "",
        eventPrice: row.eventPrice || "",
        ticketLink: row.ticketLink || "",
        genre: row.genre || null,
      };
      imageFile.value = null;
      // Visa existerande bild som preview - byts om användaren väljer ny fil
      imagePreview.value = row.imageUrl || "";
      selectedFiles.value = null;
      isDialogOpen.value = true;
    };

    const closeDialog = () => {
      isDialogOpen.value = false;
      editingId.value = null;
      form.value = emptyForm();
      imageFile.value = null;
      imagePreview.value = "";
      selectedFiles.value = null;
    };

    const buildFormData = () => {
      const fd = new FormData();
      fd.append("title", form.value.title);
      fd.append("date", form.value.date);
      if (form.value.hasEventLink) {
        fd.append("link", form.value.link);
        // Skicka tomma info-fält så backend rensar dem om vi gick från
        // info-läge till länk-läge i edit-mode
        fd.append("eventInfo", "");
        fd.append("eventPrice", "");
        fd.append("ticketLink", "");
      } else {
        fd.append("link", "");
        fd.append("eventInfo", form.value.eventInfo);
        fd.append("eventPrice", form.value.eventPrice);
        fd.append("ticketLink", form.value.ticketLink || "");
      }
      // Genre är frivilligt - skicka tom string om inget valt så backend
      // sparar null (vilket utelämnar genre från det publika flödet).
      fd.append("genre", form.value.genre || "");
      if (imageFile.value) {
        fd.append("image", imageFile.value);
      }
      return fd;
    };

    const submitForm = async () => {
      saving.value = true;
      try {
        const config = {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        };
        const fd = buildFormData();

        if (isEditMode.value) {
          await axios.patch(
            process.env.VUE_APP_API_URL +
              "events/external/" +
              editingId.value,
            fd,
            config
          );
          $q.notify({ type: "positive", message: "Eventet har uppdaterats!" });
        } else {
          await axios.post(
            process.env.VUE_APP_API_URL + "events/external",
            fd,
            config
          );
          $q.notify({ type: "positive", message: "Eventet har skapats!" });
        }
        closeDialog();
      } catch (err) {
        if (!handleAuthError(err)) {
          $q.notify({
            type: "negative",
            message:
              err.response?.data?.message ||
              (isEditMode.value
                ? "Misslyckades med att uppdatera event"
                : "Misslyckades med att skapa event"),
          });
        }
      } finally {
        saving.value = false;
        fetchData();
      }
    };

    const confirmDelete = (id) => {
      $q.dialog({
        title: "Bekräfta",
        message: "Är du säker på att du vill radera detta event?",
        ok: { label: "Ja", color: "primary" },
        cancel: { label: "Nej", flat: true, color: "grey-7" },
      }).onOk(async () => {
        try {
          await axios.delete(
            process.env.VUE_APP_API_URL + "events/external/" + id,
            { headers: authHeader() }
          );
          events.value = events.value.filter((event) => event._id !== id);
          $q.notify({ type: "positive", message: "Eventet har raderats!" });
        } catch (err) {
          if (!handleAuthError(err)) {
            $q.notify({
              type: "negative",
              message:
                err.response?.data?.message || "Kunde inte radera eventet",
            });
          }
        }
      });
    };

    onMounted(() => {
      fetchData();
      fetchGenres();
    });

    return {
      events,
      columns,
      form,
      genreOptions,
      isDialogOpen,
      isEditMode,
      saving,
      selectedFiles,
      imagePreview,
      eventForm,
      imageRules,
      onValidationError,
      openCreate,
      openEdit,
      closeDialog,
      submitForm,
      confirmDelete,
    };
  },
};
</script>

<style scoped>
.my-events-title {
  font-family: "Playfair Display", serif;
  font-size: 1.75rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-text);
  margin: 0;
}

/* Dialog-rubrik följer samma typografi som övriga sajten */
:deep(.q-dialog .text-h6) {
  font-family: "Playfair Display", serif;
  font-weight: 900;
  letter-spacing: 0.01em;
  color: var(--color-text);
}

/* CTA-knappar (Skapa event, Skicka, Spara) får sajtens uppercase/letterspacing
   så de matchar t.ex. ticket-knappen i event-info-drawer:n */
:deep(.q-btn[color="primary"]:not(.q-btn--flat)) {
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
}
</style>
